import { useAppDispatch } from 'app/hooks';
import { useNavigate } from 'react-router-dom';
import { useAddCompanyOwnerMutation } from '../services';
import { COMPANY_OWNER_CREATE_FIELDS_SCHEMA, COMPANY_OWNER_INITIAL_FIELDS } from '../constants/company-owner-fields';
import { FormEvent, useCallback } from 'react';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { removeEmpty, trimValues } from 'utils/object-helpers';
import { CompanyOwnerDto } from '../types/dtos';
import { useForm } from 'hooks/useForm';

const useController = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [addCompanyOwner, { isLoading: isAddCompanyOwnerLoading }] = useAddCompanyOwnerMutation();
    const {
        formData,
        handleChange: onChange,
        handleSubmit,
        errors,
    } = useForm({ defaultValues: COMPANY_OWNER_INITIAL_FIELDS, validationSchema: COMPANY_OWNER_CREATE_FIELDS_SCHEMA });

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'company_owner.COMPANY_OWNER_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        const data = {
            ...formData,
            fullName: `${formData.firstName} ${formData.lastName}`,
            account: { email: formData.email, password: formData.password },
        };
        delete data.firstName;
        delete data.lastName;
        delete data.email;
        delete data.password;

        return trimValues(removeEmpty(data)) as CompanyOwnerDto;
    }, [formData]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();
                    await addCompanyOwner(payload).unwrap();

                    handleSuccess();
                    navigate('../company-owners');
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [addCompanyOwner, handleErrors, handleSuccess, handleSubmit, navigate, getPayload],
    );

    const onCancel = useCallback(() => {
        navigate('../company-owners');
    }, [navigate]);

    return {
        formData,
        onChange,
        onSubmit,
        onCancel,
        isLoading: false,
        isAddCompanyOwnerLoading: isAddCompanyOwnerLoading,
        errors,
    };
};

export default useController;
